import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { StatusLabel } from '@/components/generals/status-label'
import { ConceptScreen } from '@/components/landing/concept-screen'

interface HeroSceneLiteProps {
    className?: string
}

// Lightweight hero composition built without the 3D engine: layered BUCKY lettering on a reflective
// navy tabletop with the Play-to-Pitch concept tablet behind it. Used on phones, portrait tablets,
// with reduced motion, and whenever the 3D scene is unavailable.
export const HeroSceneLite: FC<HeroSceneLiteProps> = ({ className }): ReactNode => {
    return (
        <div className={cn('hero-lite relative', className)}>
            <div className='@container relative aspect-[5/4] w-full overflow-hidden rounded-3xl'>
                <div aria-hidden className='absolute inset-0 bg-[radial-gradient(90%_70%_at_60%_30%,#1B3350,#0D1C2D_55%,#08131F)]' />

                <div className='hero-pullback absolute inset-0 origin-[24%_78%]'>
                    <div aria-hidden className='absolute inset-x-0 bottom-0 h-[46%] bg-[radial-gradient(70%_80%_at_40%_0%,#1B3350,#0D1C2D_55%,#08131F_90%)]' />
                    <div aria-hidden className='absolute inset-x-0 top-[54%] h-px bg-linear-to-r from-transparent via-soft-white/15 to-transparent' />

                    <div aria-hidden className='absolute top-[7%] right-[4%] w-[64%] [perspective:1200px]'>
                        <div className='rounded-[3cqw] bg-[linear-gradient(145deg,#3A3A3A,#1F1F1F_40%,#121212)] p-[1.4cqw] shadow-[0_30px_60px_-20px_rgb(0_0_0/0.85)] [transform:rotateY(-16deg)_rotateX(6deg)]'>
                            <div className='@container overflow-hidden rounded-[1.6cqw]'>
                                <ConceptScreen
                                    stage='engage'
                                    language='en'
                                    score={120}
                                    speaking
                                    className='rounded-none'
                                />
                            </div>
                        </div>
                    </div>

                    <div aria-hidden className='absolute bottom-[30%] left-[10%] size-[34%] rounded-full bg-orange/25 blur-[6cqw]' />

                    <div aria-hidden className='absolute bottom-[14%] left-[6%]'>
                        <p className='bucky-3d-text font-heading text-[17cqw] leading-none font-extrabold tracking-[-0.02em]'>
                            BUCKY
                        </p>

                        <p className='bucky-3d-reflection font-heading text-[17cqw] leading-none font-extrabold tracking-[-0.02em]'>
                            BUCKY
                        </p>
                    </div>
                </div>

                <div aria-hidden className='pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_50%_45%,transparent_55%,rgb(8_19_31/0.85))]' />

                <StatusLabel status='concept' className='absolute right-3 bottom-3' />
            </div>
        </div>
    )
}
